import { Link, RouteComponentProps } from "@reach/router";
import React, { useState } from "react";
import styled from "styled-components";
import { Button } from "../components/Button";
import Game from "../components/Game";
import { TitleHeading } from "../components/Heading";
import MediaSettings from "../components/MediaSettings";
import { requiresAuth } from "../context/AuthContext";
import { GameRoomProvider, useGameRoom } from "../context/GameRoomContext";
import { GameRoomStateContext } from "../context/GameRoomState";
import useRoomToken from "../hooks/useRoomToken";
import { WelcomeCard } from "./JoinCard";
import { Wrapper } from "./Wrapper";

const WelcomeMessage = styled.div`
  margin: var(--spacing-default) 0;
`;

const PlayerAvatar = styled.div`
  margin: var(--spacing-default) 0;
  padding: var(--spacing-default);
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

const MAX_PLAYERS = 8;

type Props = RouteComponentProps<{ roomId: string }>;
const GameRoom: React.FC<Props> = ({ roomId }) => {
  const { gameRoom } = useGameRoom();
  const [mediaReady, setMediaReady] = useState(false);
  const { roomToken, fetchToken } = useRoomToken(roomId!);
  const [isJoining, setIsJoining] = useState(false);
  const join = async () => {
    setIsJoining(false);
    let t = roomToken;
    try {
      if (!t) {
        t = await fetchToken();
      }
      if (t) {
        gameRoom?.join();
      }
    } catch (err) {
      console.error("There has been an error trying to join!", err);
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <Wrapper>
      {!gameRoom?.hasJoined && (
        <TitleHeading>
          <Link to="/">President.game</Link>
        </TitleHeading>
      )}
      {!gameRoom?.hasJoined && (
        <WelcomeCard>
          <MediaSettings onMediaReady={() => setMediaReady(true)} />
          {gameRoom?.players?.length ? (
            <>
              <WelcomeMessage>
                Join {gameRoom?.players.length} other
                {gameRoom?.players.length === 1 ? "" : "s"}!
              </WelcomeMessage>
              {gameRoom.players.map((player) => (
                <PlayerAvatar key={player.id} data-id={player.id}>
                  {player.name}
                </PlayerAvatar>
              ))}
            </>
          ) : (
            <WelcomeMessage>You are the first one here!</WelcomeMessage>
          )}
          {(gameRoom?.players?.length || 0) <= MAX_PLAYERS && (
            <Button disabled={!mediaReady || isJoining} onClick={join}>
              Join
            </Button>
          )}
        </WelcomeCard>
      )}
      {gameRoom?.hasJoined && (
        <GameRoomStateContext.Provider value={gameRoom.state!}>
          <Game gameRoomState={gameRoom.state!} />
        </GameRoomStateContext.Provider>
      )}
    </Wrapper>
  );
};

export default requiresAuth((props: Props) => (
  <GameRoomProvider roomId={props.roomId!}>
    <GameRoom {...props} />
  </GameRoomProvider>
));
